import { useEffect, useState, useContext } from "react";
import { X, Upload, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  uploadProcurementFile,
  getProcurementFiles,
  deleteProcurementFileById,
} from "../../../Services/procurementMRRService";
import { Spinner } from "../../../Components/Common";
import { extractFileName } from "../../../Utils/extractFileName";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { PMRR_STATUS, TOAST_MESSAGES } from "../constants";
import { UserContext } from "../../../Context";
import { ROLES } from "../../../Constants/Strings";

const ProcurementMRRActions = ({ mrrId, onClose, status }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const { user } = useContext(UserContext);

  const fetchFiles = async () => {
    try {
      const res = await getProcurementFiles(mrrId);
      setFiles(res?.data?.results || []);
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_LOAD_FILES
      );
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    formData.append("mrr", mrrId);

    setLoading(true);
    try {
      const res = await uploadProcurementFile(mrrId, formData);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOAD_SUCCESS);
        fetchFiles();
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FILE_UPLOAD_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    setLoading(true);
    try {
      await deleteProcurementFileById(mrrId, fileId);
      toast.success(TOAST_MESSAGES.FILE_DELETE_SUCCESS);
      fetchFiles();
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FILE_DELETE_FAILED
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [mrrId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white w-[90%] md:w-[500px] rounded-xl p-4 shadow-lg relative">
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        <h2 className="text-lg font-semibold mb-3 text-primary">
         {(user.role === ROLES.PROCUREMENT_INCHARGE) && (status === PMRR_STATUS.PENDING_PROCUREMENT) ? "Upload" : "View" } Files
        </h2>

        {/* Upload Section */}
        {user.role === ROLES.PROCUREMENT_INCHARGE && status === PMRR_STATUS.PENDING_PROCUREMENT && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <label className="cursor-pointer flex flex-col items-center">
              <Upload className="h-8 w-8 text-blue-500 mb-2" />
              <span className="text-gray-700 font-medium">
                Click to upload file
              </span>
              <input type="file" className="hidden" onChange={handleUpload} />
            </label>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner size={4} />
          </div>
        ) : (
          <div className="mt-4 max-h-[250px] overflow-y-auto">
            {files.length === 0 ? (
              <p className="text-gray-500 text-center">
                No files uploaded yet.
              </p>
            ) : (
              <ul className="divide-y">
                {files.map((file) => (
                  <>
                    <li
                      key={file.id}
                      className="flex justify-between items-center py-2"
                    >
                      <a
                        href={file.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm truncate"
                      >
                        {extractFileName(file.file_url)}
                      </a>
                      <button
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(file.id)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </li>
                  </>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcurementMRRActions;
